import { useEffect, useState } from "react"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { ECourierVehicleType } from "../../../Enum/ECourierVehicleType"
import { useCourierAccount } from "../../../hooks/Courier/useCourierAccount"
import { useCourierViewContext } from "../../../context/useCourierViewContext"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import THEME from "../../../configuration/Theme"
import { ButtonIcon } from "../../../components/Button/Button"
import { faAddressCard, faClose, faIdCard, faKey, faPhone, faSave, faUser } from "@fortawesome/free-solid-svg-icons"
import InputField from "../../../components/InputField/InputField"
import GetSpace from "../../../utils/space"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

interface CourierAccountPopupProps {
  isOpen: boolean
  onClose: () => void
}

export const CourierAccountPopup = ({ isOpen, onClose }: CourierAccountPopupProps) => {
  const { auth, updateCourier } = useCourierViewContext()

  const { handleUpdateProfile, handleChangePassword } = useCourierAccount()

  const [profileForm, setProfileForm] = useState({
    name: auth.courier?.personEntity.name ?? "",
    phone: auth.courier?.personEntity.phone ?? "",
    vehicleType: auth.courier?.vehicleType ?? ECourierVehicleType.MotorCycle,
  })

  const [passwordForm, setPasswordForm] = useState({ currentPassword: "", newPassword: "" })

  useEffect(() => {
    document.body.classList.toggle("overflow-hidden", isOpen)
    return () => document.body.classList.remove("overflow-hidden")
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    setProfileForm({
      name: auth.courier?.personEntity.name ?? "",
      phone: auth.courier?.personEntity.phone ?? "",
      vehicleType: auth.courier?.vehicleType ?? ECourierVehicleType.MotorCycle,
    })
    setPasswordForm({ currentPassword: "", newPassword: "" })
  }, [isOpen, auth.courier])

  if (!isOpen) return null

  const vehicleOptions = Object.entries(ECourierVehicleType)
    .filter(([, value]) => typeof value === "number")
    .map(([label]) => label)

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              title="Minha conta de entregador"
              icon={faAddressCard}
              iconColor={THEME.colors.iconBGColor}
              children={
                <ButtonIcon
                  sizeNumber={30}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  size="small"
                  descriptionKey="interactionDescriptions:pages.delivery.components.courierAccountPopup.button.onClose"
                />
              }
            />

            <GetSpace top={5} />
            <Animate type={AnimationTypes.slideUpIn} duration="1.2s">
              <div className="space-y-3 p-1">
                <Animate type={AnimationTypes.slideUpIn} duration="1.5s">
                  <section className="rounded-lg border-2 border-slate-200 bg-white p-3 text-sm text-slate-700">
                    <p className="font-bold text-slate-800">
                      Responsabilidade financeira entre restaurante e entregador
                    </p>

                    <p className="mt-1">
                      O sistema apenas registra conferências. Quem negocia e paga pelas entregas é sempre o restaurante
                      responsável.
                    </p>
                  </section>
                </Animate>

                <Animate type={AnimationTypes.slideUpIn} duration="2s">
                  <div className="grid gap-3 md:grid-cols-2">
                    <section className="space-y-3 rounded-lg border-2 border-slate-200 bg-white p-3">
                      <h3 className="text-sm font-bold text-slate-800">Meus dados</h3>

                      <InputField
                        type="text"
                        value={profileForm.name}
                        onChange={(e) => setProfileForm((prev) => ({ ...prev, name: e.target.value }))}
                        placeholder="Seu nome"
                        icon={faUser}
                        required
                        descriptionKey="interactionDescriptions:pages.delivery.components.courierAccountPopup.input.seuNome"
                      />

                      <InputField
                        type="text"
                        value={profileForm.phone}
                        onChange={(e) => setProfileForm((prev) => ({ ...prev, phone: e.target.value }))}
                        placeholder="Seu telefone"
                        icon={faPhone}
                        required
                        descriptionKey="interactionDescriptions:pages.delivery.components.courierAccountPopup.input.seuTelefone"
                      />

                      <div>
                        <div className="mb-1 text-sm font-medium text-gray-600 ml-2">Veículo</div>
                        <GenericDropDown
                          data={vehicleOptions}
                          initialState={profileForm.vehicleType}
                          handlerFuncion={(e) =>
                            setProfileForm((prev) => ({
                              ...prev,
                              vehicleType: Number(
                                ECourierVehicleType[e.target.value as keyof typeof ECourierVehicleType],
                              ),
                            }))
                          }
                          descriptionKey="interactionDescriptions:pages.delivery.components.courierAccountPopup.select.select"
                        />
                      </div>

                      <button
                        onClick={() =>
                          handleUpdateProfile(profileForm, (courier) => {
                            updateCourier(courier)
                            onClose()
                          })
                        }
                        className="w-full rounded-lg border border-orange-200 bg-orange-100 py-2 text-sm font-semibold text-orange-800"
                        data-interaction-description-key="interactionDescriptions:pages.delivery.components.courierAccountPopup.button.handleUpdateProfileProfileFormCourier"
                      >
                        <FontAwesomeIcon icon={faSave} className="mr-2" />
                        Salvar meus dados
                      </button>
                    </section>

                    <section className="space-y-3 rounded-lg border border-slate-200 bg-white p-3">
                      <h3 className="text-sm font-bold text-slate-800">Trocar senha</h3>

                      <InputField
                        type="password"
                        value={passwordForm.currentPassword}
                        onChange={(e) => setPasswordForm((prev) => ({ ...prev, currentPassword: e.target.value }))}
                        placeholder="Senha atual"
                        icon={faIdCard}
                        required
                        descriptionKey="interactionDescriptions:pages.delivery.components.courierAccountPopup.input.senhaAtual"
                      />

                      <InputField
                        type="password"
                        value={passwordForm.newPassword}
                        onChange={(e) => setPasswordForm((prev) => ({ ...prev, newPassword: e.target.value }))}
                        placeholder="Nova senha"
                        icon={faKey}
                        required
                        descriptionKey="interactionDescriptions:pages.delivery.components.courierAccountPopup.input.novaSenha"
                      />

                      <button
                        onClick={() =>
                          handleChangePassword(passwordForm, () => {
                            setPasswordForm({ currentPassword: "", newPassword: "" })
                            onClose()
                          })
                        }
                        className="w-full rounded-lg border border-slate-200 bg-slate-100 py-2 text-sm font-semibold text-slate-700"
                        data-interaction-description-key="interactionDescriptions:pages.delivery.components.courierAccountPopup.button.handleChangePasswordPasswordFormOnClose"
                      >
                        <FontAwesomeIcon icon={faSave} className="mr-2" />
                        Atualizar senha
                      </button>
                    </section>
                  </div>
                </Animate>
              </div>
            </Animate>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
